import { ReactNode, createContext, useContext, useState } from "react";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

type ConfirmOptions = {
  title?: string;
  content: ReactNode;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  showConfirm?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmDialogContext = createContext<ConfirmFn | null>(null);

export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error("useConfirmDialog must be used within ConfirmDialogProvider");
  }
  return context;
};

export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: "",
    content: "",
    confirmText: "ok",
    cancelText: "cancel",
    showCancel: true,
    showConfirm: true,
  });
  const [resolve, setResolve] = useState<(value: boolean) => void>();

  const showDialog: ConfirmFn = (opts) => {
    setOptions({ ...options, ...opts });
    setOpen(true);
    return new Promise<boolean>((res) => {
      setResolve(() => res);
    });
  };

  const handleClose = (result: boolean) => {
    setOpen(false);
    resolve?.(result);
  };

  return (
    <ConfirmDialogContext.Provider value={showDialog}>
      {children}
      <Dialog open={open} onClose={() => handleClose(false)}>
        {options.title && <DialogTitle>{options.title}</DialogTitle>}

        <DialogContent>{options.content}</DialogContent>
        {options.showCancel || options.showConfirm ? (
          <DialogActions>
            {options.showCancel && (
              <Button onClick={() => handleClose(false)}>{options.cancelText} </Button>
            )}
            {options.showConfirm && (
              <Button onClick={() => handleClose(true)} autoFocus>
                {options.confirmText}
              </Button>
            )}
          </DialogActions>
        ) : null}
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
};
